/* eslint-disable react/jsx-no-undef */
import { FGCustomPanel, FGMultiSuggestInput, FGTextInput, FieldGroup, IDataTableColumn, Loading } from "nsitools-react";
import * as React from "react";
import { useParams } from "react-router";

import {
  BreedGroupApi,
  BreedGroupGridDto,
  BreedGroupJudgeSaveDto,
  BreedGroupSearch,
  BreedGroupsJudgeSaveDto,
  PersonApi,
} from "../../../../../api";
import { ERoutes } from "../../../../../ERoutes";
import { SmallFormGenerator, SRSHCustomSelector } from "../../../../../components";
import { useGlobalData } from "../../../../../contexts";
import { useApiService, useCrudApi, useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { useReferential } from "../../../../../hooks/useReferential";

export interface IPersonBreedGroupJudgePageProps {
  onUpdated: (id) => void;
}

export const PersonBreedGroupJudgePage: React.FunctionComponent<IPersonBreedGroupJudgePageProps> = ({ onUpdated }) => {
  const breedGroupApi = useApiService(BreedGroupApi);
  const personApi = useApiService(PersonApi);
  const { id, state } = useParams<{ id: string; state: string }>();
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();

  const { data, loading, saveItem, saving } = useCrudApi<BreedGroupsJudgeSaveDto>(
    {
      getApiFn: () => personApi.getJudgeLinkedBreedsIds(+id),
      saveApiFn: (d) => personApi.saveBreedGroupsForJudge(d),
      onSavedRoute: (d) => `${ERoutes.people}/${+id}/judge/edit`,
    },
    [id]
  );

  const save = React.useCallback(
    async (d: BreedGroupsJudgeSaveDto) => {
      await saveItem(d);
      onUpdated("PersonBreedGroupJudgePage" + new Date().getTime());
    },
    [onUpdated, saveItem]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => t(ETLCodes.BreedGroupLabel),
        autoFitContent: true,
        fieldName: "name",
        render: (row: BreedGroupGridDto) => (
          <div>
            {row.breedGroupNumber}. {row.name}
          </div>
        ),
      },
    ],
    [t]
  );

  const searchBreedGroupIdsFunc = React.useCallback(
    (sObj) =>
      breedGroupApi.getIds(
        BreedGroupSearch.fromJS({
          ...sObj,
          languageId: currentLanguage.languageId,
        })
      ),
    [breedGroupApi, currentLanguage]
  );

  const searchBreedGroupFunc = React.useCallback(
    (sObj) =>
      breedGroupApi.baseSearch(
        BreedGroupSearch.fromJS({
          ...sObj,
          languageId: currentLanguage.languageId,
        })
      ),
    [breedGroupApi, currentLanguage]
  );

  return (
    <Loading isLoading={loading}>
      <SmallFormGenerator
        initialValues={data}
        onSubmit={save}
        editMode={state === "edit"}
        loading={loading}
        saving={saving}
      >
        <FieldGroup columns={1} fieldsetProps={{ title: t(ETLCodes.BreedGroups) }}>
          <FGCustomPanel>
            {(ctx) => (
              <SRSHCustomSelector
                linkedIds={ctx.formik.values?.breedGroupIds}
                columns={columns}
                onLinkedIdsChanged={(ids) => ctx.formik.setFieldValue("breedGroupIds", ids)}
                idField="breedGroupId"
                searchFunction={searchBreedGroupFunc}
                searchIdsFunc={searchBreedGroupIdsFunc}
                editMode={state === "edit"}
              />
            )}
          </FGCustomPanel>
        </FieldGroup>
      </SmallFormGenerator>
    </Loading>
  );
};
